/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/AdminLayout.tsx
 * DESCRIPTION: Layout wrapper for admin pages with Clerk-based
 *              authorization. Shows access denied message if user
 *              lacks admin role in Clerk metadata.
 *
 * AUTHORIZATION:
 * - Checks isAdminAuthenticated from AdminAuthContext
 * - Context reads user.publicMetadata.role === 'admin' from Clerk
 * - No password required; access tied to Clerk user identity
 *
 * MIGRATION:
 * v1.0.0 (2026-03-27): Password-based admin auth with modal
 * v2.0.0 (2026-09-23): Clerk metadata-based authorization
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2026-03-27
 * @updated 2026-09-23 - Replaced password modal with Clerk metadata
 *                       authorization check, added access denied UI
 * ─────────────────────────────────────────────────────────────────────
 */

import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Shield, Lock } from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  description?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, description }) => {
  const { isAdminAuthenticated, isAdminLoading } = useAdminAuth();

  // Loading state
  if (isAdminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Access denied state
  if (!isAdminAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You do not have permission to access the admin dashboard.
            </p>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                <strong>Note:</strong> Admin access is granted based on your user role.
                Contact your system administrator if you believe this is an error.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authorized state
  return (
    <div className="p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {title}
              </h1>
              {description && (
                <p className="text-gray-600 dark:text-gray-400">
                  {description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">Admin Access</span>
            </div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;